import { useCadModelObjectLimitedBoundingBox } from "@/hooks/use-object-bounding-box";
import { useCached3DObject } from "@/object-cache";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import {
  selectCloudCadAlignmentStep,
  selectCloudForCadAlignment,
} from "@/store/modes/cloud-cad-alignment-mode-selectors";
import { CloudCadAlignmentStep } from "@/store/modes/cloud-cad-alignment-mode-slice";
import { useAppSelector } from "@/store/store-hooks";
import { assert } from "@faro-lotv/foundation";
import { isIElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useEffect, useMemo, type JSX } from "react";
import { Box3 } from "three/src/math/Box3";
import { AlignCloudCadScene } from "./cloud-cad-alignment-scene";
import { CloudCadElevationScene } from "./cloud-cad-elevation-scene";

/** @returns Rendered element for both steps of the cloud-CAD alignment */
export function CloudCadAlignmentModeScene(): JSX.Element | null {
  const activeCloudID = useAppSelector(selectCloudForCadAlignment);
  const activeCloud = useAppSelector(selectIElement(activeCloudID));

  const activeCad = useAppSelector(selectActiveCadModel);
  assert(
    activeCad && activeCloud && isIElementGenericPointCloudStream(activeCloud),
    "CAD Alignment Mode requires a CAD model and cloud",
  );

  const cadModel = useCached3DObject(activeCad, "CloudCadAlignmentModeScene");
  useEffect(() => {
    // reset any highlight state from the cadModel
    for (const objectId of cadModel.objectIds) {
      cadModel.setObjectHighlighted(objectId, false);
    }
  }, [cadModel]);

  // get the cad model bounding box in world
  const cadBox = useCadModelObjectLimitedBoundingBox(
    cadModel,
    cadModel.iElement.id,
  );
  const cadBoundingBox = useMemo(
    () => cadBox.boundingBox ?? new Box3(),
    [cadBox.boundingBox],
  );

  const step = useAppSelector(selectCloudCadAlignmentStep);
  switch (step) {
    case CloudCadAlignmentStep.setElevations:
      return (
        <CloudCadElevationScene
          activeCad={activeCad}
          cadBox={cadBoundingBox}
          selectedCloud={activeCloud}
        />
      );
    case CloudCadAlignmentStep.alignIn2d:
      return (
        <AlignCloudCadScene
          activeCloud={activeCloud}
          activeCad={activeCad}
          cadBox={cadBoundingBox}
        />
      );
  }
}
